import type { Alignment, Side } from 'driver.js';

export const PLATFORM_ONBOARDING_STORAGE_PREFIX = 'vertice.onboarding.v1';

export interface PlatformOnboardingStatus {
  whatsAppVerificationRequired: boolean;
  hasWhatsAppUserId: boolean;
  hasCpf: boolean;
  hasUsableConnection: boolean;
  tutorialCompleted: boolean;
  subscriptionBlocked?: boolean;
}

export type ConnectionStatus = {
  status?: string | null;
  accounts?: Array<{ id: string }>;
};

export type SubscriptionAccessStatus = {
  status: string;
  hasManagedSubscription: boolean;
};

export type PlatformProductTourStepId =
  | 'home'
  | 'navigation'
  | 'connections'
  | 'flow'
  | 'invoices'
  | 'planning'
  | 'goals'
  | 'recurring'
  | 'investments'
  | 'ai'
  | 'problem-report';

export type PlatformProductTourStep = {
  id: PlatformProductTourStepId;
  route?: string;
  selector: string;
  mobileSelector?: string;
  title: string;
  body: string;
  side?: Side;
  align?: Alignment;
  mobileSide?: Side;
  mobileAlign?: Alignment;
};

const BLOCKED_CONNECTION_STATUSES = new Set([
  'LOGIN_ERROR',
  'WAITING_USER_INPUT',
  'WAITING_USER_ACTION',
]);

export const PLATFORM_PRODUCT_TOUR_STEPS: PlatformProductTourStep[] = [
  {
    id: 'home',
    route: '/',
    selector: '[data-onboarding-home], [data-onboarding-dashboard]',
    mobileSelector: '[data-onboarding-home], [data-onboarding-dashboard]',
    title: 'Início',
    body: 'A página inicial resume suas finanças: quanto está disponível para gastar, o ritmo de gastos, faturas em aberto, patrimônio e metas — o essencial em um só lugar.',
    side: 'bottom',
    align: 'start',
    mobileSide: 'bottom',
    mobileAlign: 'start',
  },
  {
    id: 'navigation',
    route: '/',
    selector: '[data-onboarding-sidebar]',
    mobileSelector: '[data-bottom-nav-tab="transactions"]',
    title: 'Navegação principal',
    body: 'No desktop, use a barra lateral. No celular, a barra inferior concentra os caminhos mais usados.',
    side: 'right',
    align: 'start',
    mobileSide: 'top',
    mobileAlign: 'center',
  },
  {
    id: 'connections',
    route: '/connections',
    selector: '[data-onboarding-connection-cta], [data-onboarding-connections-page], [data-onboarding-nav="connections"]',
    mobileSelector: '[data-onboarding-connection-cta], [data-bottom-nav-tab="home"], [data-onboarding-connections-page]',
    title: 'Conexões Open Finance',
    body: 'Aqui você conecta, renova e acompanha as instituições que alimentam seus dados.',
    side: 'bottom',
    align: 'end',
    mobileSide: 'bottom',
    mobileAlign: 'center',
  },
  {
    id: 'flow',
    route: '/transactions',
    selector: '[data-onboarding-transactions-page], [data-onboarding-nav="transactions"]',
    mobileSelector: '[data-bottom-nav-tab="transactions"], [data-onboarding-transactions-page]',
    title: 'Transações',
    body: 'Use Transações para investigar entradas, saídas, categorias e histórico de cada conta.',
    side: 'bottom',
    align: 'start',
    mobileSide: 'top',
    mobileAlign: 'center',
  },
  {
    id: 'invoices',
    route: '/invoices',
    selector: '[data-onboarding-invoices-page], [data-onboarding-nav="invoices"]',
    mobileSelector: '[data-bottom-nav-tab="faturas"], [data-onboarding-invoices-page]',
    title: 'Faturas',
    body: 'Acompanhe ciclos do cartão, vencimentos, parcelas futuras e detalhes de cada fatura.',
    side: 'bottom',
    align: 'start',
    mobileSide: 'top',
    mobileAlign: 'center',
  },
  {
    id: 'planning',
    route: '/budget',
    selector: '[data-onboarding-budget-page], [data-onboarding-nav="budget"]',
    mobileSelector: '[data-bottom-nav-tab="budget"], [data-onboarding-budget-page]',
    title: 'Categorias',
    body: 'Todas as suas categorias com o gasto do mês e limites mensais — confirme lançamentos novos na fila A revisar.',
    side: 'bottom',
    align: 'start',
    mobileSide: 'top',
    mobileAlign: 'center',
  },
  {
    id: 'goals',
    route: '/metas',
    selector: '[data-onboarding-goals-page], [data-onboarding-nav="goals"]',
    mobileSelector: '[data-bottom-nav-tab="budget"], [data-onboarding-goals-page]',
    title: 'Metas',
    body: 'Metas organizam reserva, objetivos de poupança e quitação de dívidas em uma linha de progresso.',
    side: 'bottom',
    align: 'start',
    mobileSide: 'top',
    mobileAlign: 'center',
  },
  {
    id: 'recurring',
    route: '/recurring-payments',
    selector: '[data-onboarding-recurring-page], [data-onboarding-nav="recurring"]',
    mobileSelector: '[data-bottom-nav-tab="budget"], [data-onboarding-recurring-page]',
    title: 'Recorrentes',
    body: 'Recorrentes mostra assinaturas, contas fixas e compromissos mensais detectados automaticamente.',
    side: 'bottom',
    align: 'start',
    mobileSide: 'top',
    mobileAlign: 'center',
  },
  {
    id: 'investments',
    route: '/investments',
    selector: '[data-onboarding-investments-page], [data-onboarding-nav="investments"]',
    mobileSelector: '[data-bottom-nav-tab="investments"], [data-onboarding-investments-page]',
    title: 'Investimentos',
    body: 'A área de investimentos mostra posições, movimentações e vencimentos importados das conexões.',
    side: 'bottom',
    align: 'start',
    mobileSide: 'top',
    mobileAlign: 'center',
  },
  {
    id: 'ai',
    selector: '[data-onboarding-chat]',
    mobileSelector: '[data-onboarding-chat]',
    title: 'Vertice AI',
    body: 'Abra o assistente para perguntar sobre gastos, orçamento, faturas, metas e investimentos com o contexto da tela.',
    side: 'left',
    align: 'end',
    mobileSide: 'top',
    mobileAlign: 'end',
  },
];

// On desktop the entry lives in the sidebar; on mobile it sits in the "Início" navigation drawer,
// whose contents aren't mounted while closed — so the mobile step anchors to the always-present
// "Início" tab instead of the in-drawer button.
const PROBLEM_REPORT_PRODUCT_TOUR_STEP: PlatformProductTourStep = {
  id: 'problem-report',
  route: '/',
  selector: '[data-onboarding-problem-report]',
  mobileSelector: '[data-bottom-nav-tab="home"]',
  title: 'Relatar um problema',
  body: 'Encontrou um erro ou tem uma sugestão? Você pode relatar um problema quando quiser: no celular, pelo menu Início; no computador, pela barra lateral.',
  side: 'right',
  align: 'start',
  mobileSide: 'top',
  mobileAlign: 'center',
};

export function buildPlatformProductTourSteps(
  problemReportEnabled: boolean,
): PlatformProductTourStep[] {
  return problemReportEnabled
    ? [...PLATFORM_PRODUCT_TOUR_STEPS, PROBLEM_REPORT_PRODUCT_TOUR_STEP]
    : PLATFORM_PRODUCT_TOUR_STEPS;
}

export function platformOnboardingStorageKey(userId: string): string {
  return `${PLATFORM_ONBOARDING_STORAGE_PREFIX}:${userId}`;
}

export function isUsableConnection(item: ConnectionStatus): boolean {
  const status = item.status?.trim().toUpperCase();
  if (status && BLOCKED_CONNECTION_STATUSES.has(status)) return false;
  return (item.accounts?.length ?? 0) > 0;
}

export function hasUsableConnection(items: ConnectionStatus[]): boolean {
  return items.some(isUsableConnection);
}

export function isSubscriptionAccessBlocked(subscription: SubscriptionAccessStatus | null | undefined): boolean {
  return !!subscription
    && !subscription.hasManagedSubscription
    && subscription.status.toLowerCase() === 'trialexpired';
}

function whatsappDigits(value: string): string {
  return value.replace(/\D/g, '');
}

function stripInternationalDialPrefix(digits: string): string {
  return digits.startsWith('00') ? digits.slice(2) : digits;
}

function hasExplicitBrazilCountryCode(value: string, digits: string): boolean {
  return value.trimStart().startsWith('+')
    || value.replace(/\D/g, '').startsWith('00')
    || (digits.startsWith('55') && digits.length > 11);
}

export function normalizeWhatsAppPhoneInput(value: string): string {
  const rawDigits = whatsappDigits(value);
  const digits = stripInternationalDialPrefix(rawDigits);
  if (!digits) return value.trimStart().startsWith('+') ? '+' : '';

  const hasCountryCode = hasExplicitBrazilCountryCode(value, digits);
  const normalizedDigits = digits.slice(0, hasCountryCode ? 13 : 11);
  return hasCountryCode ? `+${normalizedDigits}` : normalizedDigits;
}

export function normalizeWhatsAppPhoneForSubmit(value: string): string {
  const rawDigits = whatsappDigits(value);
  const digits = stripInternationalDialPrefix(rawDigits);
  if (!digits) return '';

  const hasCountryCode = hasExplicitBrazilCountryCode(value, digits);
  const normalizedDigits = hasCountryCode
    ? digits.slice(0, 13)
    : `55${digits.slice(0, 11)}`;
  return `+${normalizedDigits}`;
}

export function formatWhatsAppPhoneOnBlur(value: string): string {
  const normalized = normalizeWhatsAppPhoneForSubmit(value);
  const digits = whatsappDigits(normalized);
  if (!digits) return '';

  const national = digits.startsWith('55') ? digits.slice(2) : digits;
  const areaCode = national.slice(0, 2);
  const isMobileLength = national.length > 10;
  const firstPart = isMobileLength
    ? national.slice(2, 7)
    : national.slice(2, 6);
  const secondPart = isMobileLength
    ? national.slice(7, 11)
    : national.slice(6, 10);

  let formatted = '+55';
  if (areaCode) formatted += ` ${areaCode}`;
  if (firstPart) formatted += ` ${firstPart}`;
  if (secondPart) formatted += `-${secondPart}`;
  return formatted;
}

export function isWhatsAppVerificationStillValid(
  verification: { expiresAt?: string | null } | null | undefined,
  now = Date.now(),
): boolean {
  if (!verification?.expiresAt) return false;
  const expiresAt = new Date(verification.expiresAt).getTime();
  return Number.isFinite(expiresAt) && expiresAt > now;
}

/** The driver.js tour runs only once everything required is in place — the same
 * predicate the deleted step machine used, kept as one pure function so the
 * staging kill-switch (whatsAppVerificationRequired=false) cannot rot untested. */
export function shouldRunProductTour(status: PlatformOnboardingStatus): boolean {
  if (status.subscriptionBlocked) return false;
  const whatsAppReady = !status.whatsAppVerificationRequired || status.hasWhatsAppUserId;
  return whatsAppReady && status.hasCpf && status.hasUsableConnection && !status.tutorialCompleted;
}

/** The 67 assigned Brazilian area codes (DDDs). DDD 55 (Santa Maria/RS) is real —
 * it is NOT the country code here. */
export const BRAZIL_AREA_CODES: ReadonlySet<string> = new Set([
  '11', '12', '13', '14', '15', '16', '17', '18', '19',
  '21', '22', '24', '27', '28',
  '31', '32', '33', '34', '35', '37', '38',
  '41', '42', '43', '44', '45', '46', '47', '48', '49',
  '51', '53', '54', '55',
  '61', '62', '63', '64', '65', '66', '67', '68', '69',
  '71', '73', '74', '75', '77', '79',
  '81', '82', '83', '84', '85', '86', '87', '88', '89',
  '91', '92', '93', '94', '95', '96', '97', '98', '99',
]);

/** True for a full Brazilian MOBILE in E.164 (+55 DD 9XXXX-XXXX). Landlines are
 * rejected on purpose: WhatsApp needs a mobile line, and on a blocking step a
 * number that can never receive the code is a dead end with no feedback.
 * Feed it normalizeWhatsAppPhoneForSubmit's output. */
export function isValidBrazilianMobile(value: string): boolean {
  const digits = value.replace(/\D/g, '');
  return digits.length === 13
    && digits.startsWith('55')
    && BRAZIL_AREA_CODES.has(digits.slice(2, 4))
    && digits[4] === '9';
}
